import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import { auth } from './Firebase';
import { Link as RouterLink } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  IconButton,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import CustomSelect from './CustomSelect';

// Pomocnicza funkcja do formatowania daty, zachowana z oryginalnego kodu
const convertUTCToLocal = (utcValue) => {
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  } else {
    return '';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

// Przywrócony oryginalny gradient i cieniowanie z pliku PositionMatrix.js
const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.8);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Funkcje formatujące z WcSummary
const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const getDiffGradientColor = (value, maxAbsValue) => {
  // Jeśli wartość wynosi 0, zwracamy kolor bazowy
  if (value === 0 || !value || !maxAbsValue) return 'rgba(255, 255, 255, 0.3)';

  // Obliczamy stopień nasycenia (0 do 1)
  const ratio = Math.min(1, Math.abs(value) / (0.9*maxAbsValue));
  
  let r, g, b;
  const alpha = 0.3 + (ratio * 0.7);

  if (value > 0) {
    // Interpolacja w stronę zieleni
    r = Math.round(255 + (76 - 255) * ratio);
    g = Math.round(255 + (255 - 255) * ratio);
    b = Math.round(255 + (80 - 255) * ratio);
  } else {
    // Interpolacja w stronę czerwieni
    r = Math.round(255 + (255 - 255) * ratio);
    g = Math.round(255 + (67 - 255) * ratio);
    b = Math.round(255 + (54 - 255) * ratio);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};

// Funkcja pomocnicza do formatowania różnic (dodaje znak + lub -)
const formatDiffChance = (val) => {
  if (val === undefined || val === null) return '-';
  if (val === 0) return '0.0%';
  const formatted = (val * 100).toFixed(1) + '%';
  return val > 0 ? `+${formatted}` : formatted;
};

// Funkcja pomocnicza do tooltipa (wartości czyste)
const formatAbsoluteChance = (val) => {
  if (val === undefined || val === null) return '0.0%';
  return formatChance(val);
};

const calculateExpectedGroupPosition = (team) => {
  const positionProbabilities = [
    team.pctPos1 || 0,
    team.pctPos2 || 0,
    team.pctPos3 || 0,
    team.pctPos4 || 0
  ];

  return positionProbabilities.reduce(
    (sum, probability, index) => sum + probability * (index + 1),
    0
  );
};

const WcPositionMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGroup, setActiveGroup] = useState('A');
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  const competitionId = 431;

  // Nowe stany do porównywania symulacji
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [secondSimulationId, setSecondSimulationId] = useState('');
  const [secondSimData, setSecondSimData] = useState(null);
  
  const simulationOptions = simulations.map((sim) => ({
    value: sim.simulation_id,
    label: sim.displayName
  }));
  
  useEffect(() => {
    api
      .get('/competition/431/simulations')
      .then((response) => {
        const sims = response.data || [];

        // 1. Mapowanie danych
        const simList = sims.map(sim => ({
          ...sim,
          userId: sim.user_id, 
          displayName: sim.conditions && sim.conditions !== "" 
            ? `${convertUTCToLocal(sim.created_at)} (*)` 
            : convertUTCToLocal(sim.created_at)
        }));

        // 2. Filtrowanie symulacji
        const filteredSimList = simList.filter(sim => {
          const isDefault = !sim.userId || sim.userId === ""; 
          const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid;
          return isDefault || isOwned;
        });

        setSimulations(filteredSimList);

        if (filteredSimList.length > 0) {
          setSelectedSimulationId(filteredSimList[0].simulation_id);
        }
      })
      .catch((err) => {
        console.error('Błąd pobierania listy symulacji:', err);
      });
  }, [auth.currentUser]);

  useEffect(() => {
    if (!selectedSimulationId) return;

    setLoading(true);

    api
      .get(`/simulation/worldcup/latest-groups?simulationId=${selectedSimulationId}`)
      .then((response) => {
        setData(response.data);
        setLoading(false);
        setInitialLoading(false);
      })
      .catch((err) => {
        console.error('Błąd ładowania szans grupowych MŚ:', err);
        setError('Nie udało się pobrać danych symulacji.');
        setLoading(false);
        setInitialLoading(false);
      });
  }, [selectedSimulationId]);

  // Pobieranie danych dla DRUGIEJ symulacji
  useEffect(() => {
    if (!compareEnabled || !secondSimulationId) {
      setSecondSimData(null);
      return;
    }

    api
      .get(`/simulation/worldcup/latest-groups?simulationId=${secondSimulationId}`)
      .then((response) => {
        setSecondSimData(response.data);
      })
      .catch((err) => console.error("Błąd pobierania drugiej symulacji:", err));
  }, [secondSimulationId, compareEnabled]);

  const currentSimulationIndex = simulations.findIndex(
    sim => sim.simulation_id === selectedSimulationId
  );

  const groupsList = data?.teams 
    ? Array.from(new Set(data.teams.map(t => t.groupIdentifier))).sort()
    : [];

  const filteredTeams = useMemo(() => {
    return data?.teams
      ? data.teams.filter(t => t.groupIdentifier === activeGroup)
      : [];
  }, [data, activeGroup]);

  const sortedTeams = useMemo(() => {
    return [...filteredTeams]
      .map(team => ({
        ...team,
        expectedGroupPosition: calculateExpectedGroupPosition(team)
      }))
      .sort((a, b) => {
        if (a.expectedGroupPosition !== b.expectedGroupPosition) {
          return a.expectedGroupPosition - b.expectedGroupPosition;
        }

        return a.teamName.localeCompare(b.teamName, 'pl');
      });
  }, [filteredTeams]);
	
  const handleSimulationSelect = (event) => {
    const simulationId = event.target.value;
    setSelectedSimulationId(simulationId);
  };

  const handleSecondSimulationSelect = (event) => {
    const simulationId = event.target.value;
    setSecondSimulationId(simulationId);
  };

  const handlePreviousSimulation = () => {
    if (currentSimulationIndex < simulations.length - 1) {
      const newIndex = currentSimulationIndex + 1;
      setSelectedSimulationId(simulations[newIndex].simulation_id);
    }
  };

  const handleNextSimulation = () => {
    if (currentSimulationIndex > 0) {
      const newIndex = currentSimulationIndex - 1;
      setSelectedSimulationId(simulations[newIndex].simulation_id);
    }
  };	

  const handleSecondPrev = () => {
    const currentIndex = simulations.findIndex(s => s.simulation_id === secondSimulationId);
    if (currentIndex > 0) {
      setSecondSimulationId(simulations[currentIndex - 1].simulation_id);
    }
  };
  
  const handleSecondNext = () => {
    const currentIndex = simulations.findIndex(s => s.simulation_id === secondSimulationId);
    if (currentIndex < simulations.length - 1) {
      setSecondSimulationId(simulations[currentIndex + 1].simulation_id);
    }
  };

  // Obliczanie różnic w oparciu o Z-score
  const { diffTableData, maxAbsZValue } = useMemo(() => {
    const primaryData = sortedTeams; // Bazujemy na aktualnie wybranej grupie
    const secondaryData = secondSimData?.teams || [];

    if (!compareEnabled || primaryData.length === 0 || secondaryData.length === 0) {
      return { diffTableData: [], maxAbsZValue: 0 };
    }

    const secondMap = new Map(secondaryData.map(item => [item.teamName, item]));

let globalMaxAbsZ = 0;
const N = 10000; // Liczba symulacji

const tableData = primaryData.map(primaryRow => {
  const secondRow = secondMap.get(primaryRow.teamName) || {};
      const stages = ['pctPos1', 'pctPos2', 'pctPos3', 'pctPos4'];

      const stageProbabilities = stages.map(stage => {
        const pVal = primaryRow[stage] || 0; 
        const sVal = secondRow[stage] || 0; 
        const diff = pVal - sVal;

        // ---- OBLICZANIE STATYSTYKI Z-SCORE ----
        const variance1 = (pVal * (1 - pVal)) / N;
        const variance2 = (sVal * (1 - sVal)) / N;
        const standardError = Math.sqrt(variance1 + variance2);
        
        const zScore = standardError === 0 ? 0 : diff / standardError;	  

        const absZ = Math.abs(zScore);
        if (absZ > globalMaxAbsZ) {
          globalMaxAbsZ = absZ;
          if (globalMaxAbsZ > 100) {
            globalMaxAbsZ = 100;
          }
		  if (globalMaxAbsZ < 20) {
            globalMaxAbsZ = 20;
          }
        }

        return {
          diffValue: diff,
          primaryValue: pVal,
          secondaryValue: sVal,
          zValue: zScore
        };
      });

      return {
        teamId: primaryRow.teamId,
        teamName: primaryRow.teamName,
        stageProbabilities
      };
    });

    return { 
      diffTableData: tableData, 
      maxAbsZValue: globalMaxAbsZ === 0 ? 1 : globalMaxAbsZ 
    };
  }, [compareEnabled, sortedTeams, secondSimData]);

  if (initialLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">
          Ładowanie macierzy szans grup MŚ...
        </Typography>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{error || "Brak danych"}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>

      <Helmet>
        <title>Szansa na miejsca w grupach - Mistrzostwa Świata | Symuliga</title>
      </Helmet>
	
      {/* Tytuł nagłówka */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Mistrzostwa Świata - Szanse na miejsca w grupach
        </Typography>
		
        {simulations.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            <IconButton
              onClick={handlePreviousSimulation}
              disabled={currentSimulationIndex >= simulations.length - 1}
              size="small"
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <CustomSelect
              value={selectedSimulationId}
              onChange={handleSimulationSelect}
              options={simulationOptions}
            />
            <IconButton
              onClick={handleNextSimulation}
              disabled={currentSimulationIndex === 0}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Przyciski grup */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        gap: 1, 
        mb: 3,
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        width: 'fit-content',
        margin: '0 auto 24px auto'
      }}>
        {groupsList.map((groupId) => (
          <Button
            key={groupId}
            variant={activeGroup === groupId ? "contained" : "outlined"}
            onClick={() => setActiveGroup(groupId)}
            sx={{
              minWidth: { xs: '40px', sm: '50px' },
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              p: { xs: '4px 8px', sm: '6px 12px' }
            }}
          >
            {groupId}
          </Button>
        ))}
      </Box>
	  
      {simulations[currentSimulationIndex]?.conditions !== "" && (
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
          Symulacja warunkowa!<br />
          <span dangerouslySetInnerHTML={{ __html: simulations[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
        </Typography>
      )}

      {/* GŁÓWNA TABELA */}
      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        <TableContainer
          component={Paper}
          sx={{
            marginBottom: 1,
            margin: '0 auto',
            width: 'auto',
            maxWidth: 'fit-content',
            overflowX: 'auto',
            position: 'relative',
            borderRadius: '12px',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
            '&::before': {
              content: '"SymuLiga.pl"',
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
              zIndex: 0,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }} aria-label="world cup groups probability matrix table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    width: 'auto',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    padding: '6px',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  Drużyna (Grupa {activeGroup})
                </TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>1. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>2. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>3. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>4. miejsce</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTeams.map((row, rowIndex) => {
                const probabilities = [row.pctPos1, row.pctPos2, row.pctPos3, row.pctPos4];

                return (
                  <TableRow key={rowIndex}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        backgroundColor: 'background.paper',
                        width: 'auto', 
                        minWidth: 40, 
                        padding: '5px',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                      }}
                    >
                      {row.teamName}
                    </TableCell>

                    {probabilities.map((prob, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="center"
                        sx={{
                          width: 'auto', 
                          minWidth: 70, 
                          backgroundColor: getGradientColor(prob),
                          color: 'black', 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          padding: '5px',
                          textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                          transition: 'background-color 0.3s',
                          '@media (hover: hover)': {
                            '&:hover': {
                              backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
                              transform: 'scale(1.05)',
                            },
                          },
                        }}
                      >
                        {prob > 0 ? `${(prob * 100).toFixed(1)}%` : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ================= SEKCJA PORÓWNANIA SYMULACJI ================= */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
          <input
            type="checkbox"
            id="compare-checkbox"
            checked={compareEnabled}
            onChange={(e) => {
              setCompareEnabled(e.target.checked);
              if (e.target.checked && !secondSimulationId && simulations?.length > 1) {
				// Wybierz przedostatnią symulację (indeks 1, jeśli lista jest posortowana od najnowszych)
				setSecondSimulationId(simulations[1].simulation_id); 
			  } else if (e.target.checked && !secondSimulationId && simulations?.length === 1) {
				// Jeśli jest tylko jedna symulacja, wybierz ją
				setSecondSimulationId(simulations[0].simulation_id);
			  }
            }}
            style={{ transform: 'scale(1.3)', marginRight: '10px', cursor: 'pointer' }}
          />
          <label htmlFor="compare-checkbox" style={{ color: '#ffffff', fontSize: '1.1rem', cursor: 'pointer', userSelect: 'none' }}>
            Porównaj z inną symulacją
          </label>
        </Box>

        {compareEnabled && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 3 }}>
              <IconButton
                onClick={handleSecondPrev}
                disabled={simulations.findIndex(s => s.simulation_id === secondSimulationId) <= 0}
                sx={{ color: '#ffffff', '&:disabled': { color: '#555555' } }}
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              
              <CustomSelect
                value={secondSimulationId}
                onChange={handleSecondSimulationSelect}
                options={simulationOptions}
              />
              
              <IconButton
                onClick={handleSecondNext}
                disabled={simulations.findIndex(s => s.simulation_id === secondSimulationId) >= simulations.length - 1}
                sx={{ color: '#ffffff', '&:disabled': { color: '#555555' } }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            {diffTableData.length > 0 ? (
              <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    marginBottom: 1,
                    margin: '0 auto',
                    width: 'auto',
                    maxWidth: 'fit-content',
                    overflowX: 'auto',
                    position: 'relative',
                    borderRadius: '12px',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
                  }}
                >
                  <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            zIndex: 3,
                            backgroundColor: '#2c2c2c',
                            color: '#ffffff',
                            width: 'auto',
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            padding: '6px',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                          }}
                        >
                          Drużyna (Grupa {activeGroup})
                        </TableCell>
                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#2c2c2c', color: '#ffffff', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}>1. miejsce</TableCell>
                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#2c2c2c', color: '#ffffff', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}>2. miejsce</TableCell>
                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#2c2c2c', color: '#ffffff', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}>3. miejsce</TableCell>
                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#2c2c2c', color: '#ffffff', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}>4. miejsce</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diffTableData.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                              backgroundColor: 'background.paper',
                              width: 'auto',
                              minWidth: 40,
                              padding: '5px',
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            {row.teamName}
                          </TableCell>
                          
                          {row.stageProbabilities.map((probObj, colIndex) => (
                            <TableCell
                              key={`diff-cell-${colIndex}`}
                              align="center"
                              title={`${formatAbsoluteChance(probObj.secondaryValue)} --> ${formatAbsoluteChance(probObj.primaryValue)} `}
                              sx={{
                                width: 'auto',
                                minWidth: 70,
                                backgroundColor: getDiffGradientColor(probObj.zValue, maxAbsZValue),
                                color: 'black', 
                                fontWeight: 'bold',
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                padding: '5px',
                                transition: 'background-color 0.3s',
                                cursor: 'help', 
                              }}
                            >
                              {formatDiffChance(probObj.diffValue)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography sx={{ color: '#aaaaaa', textAlign: 'center', mt: 2 }}>
                Wybierz inną symulację, aby zobaczyć porównanie różnic.
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {/* ================= KONIEC SEKCJI PORÓWNANIA ================= */}

    </Box>
  );
};

export default WcPositionMatrix;
